/**
 * Measuring a model in real units, once somebody has said how big it is.
 *
 * A photogrammetry reconstruction is correct in shape and arbitrary in size.
 * Nothing here prints a millimetre figure until one real distance has been
 * supplied, and the screen says which of those two states it is in - because
 * "84.6 mm" on an uncalibrated model is not an approximation, it is a number
 * with no relationship to the object at all.
 *
 * ## The flow
 *
 * Pick two points on the surface, type what the distance between them really
 * is, and every other length follows by multiplication. After that, picking
 * two points measures instead of calibrating.
 *
 * ## What it will not claim
 *
 * The reference used to set the scale matches itself by construction - it is
 * what the factor was computed from - so its error is always zero and quoting
 * that as accuracy would be quoting a tautology. A *second*, independent
 * distance is the first number that means anything, and that is what the
 * verification step asks for.
 *
 * Scale and print-readiness are separate claims. This pipeline measures
 * neither watertightness nor manifoldness, so a verified scale never becomes
 * "ready to print".
 *
 * ## And scale is not the only thing that can be unverified
 *
 * A verified scale says the *ruler* is right. It says nothing about whether
 * the surface the ruler was laid against was ever photographed. A measurement
 * across a region a completion invented is a precise number about a guess, and
 * it looks exactly like a measurement - which is why each picked point carries
 * the provenance of the surface it landed on, and why a pair that touches
 * completed or unrecorded geometry is labelled rather than merely printed.
 *
 * `weakly_observed` counts as measured. It is a photograph of the world, and
 * refusing it would make the tool useless on exactly the regions people most
 * want to check.
 *
 * ## Keeping a measurement
 *
 * Checking three dimensions of an object against a drawing means holding three
 * numbers, and a panel that holds one turns that into paper and a pen. So a
 * measured pair can be kept as M1, M2, M3.
 *
 * Kept in this browser rather than on the server, and the screen says so.
 * Calibration *is* on the server, because it is a property of the
 * reconstruction that every screen and every export must agree about; a list
 * of things one person measured on one afternoon is a working note.
 *
 * What is stored is the two model-space points, never the converted length: a
 * measurement recorded as "84.6 mm" would be silently wrong the moment
 * somebody recalibrated, and re-converting through the current scale means a
 * corrected calibration corrects every kept measurement with it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearScaleCalibration,
  getScaleCalibration,
  setScaleCalibration,
} from "@/api/client";
import type { ScaleCalibration } from "@/api/types";
import { Button, Callout, DataList, StatusBadge } from "@/components/ui";
import { useAsync } from "@/lib/useAsync";
import type { Mesh } from "./glb";
import type { SurfaceProvenance } from "@/api/types";
import { SURFACE_PROVENANCE_TERMS } from "@/i18n/terms";
import { measurementIsVerified } from "./viewerProvenance";
import type { Vec3 } from "./viewerCamera";
import {
  boundsOf,
  diagonalOf,
  dimensionsOf,
  formatLength,
  verificationError,
  type Unit,
} from "./measurement";
import { modelDistance } from "./viewerPicking";
import {
  dropMeasurement,
  keepMeasurement,
  measurementLabel,
  readMeasurements,
  writeMeasurements,
  type KeptMeasurement,
} from "./measurementList";

const UNITS: Unit[] = ["mm", "cm", "m"];

export interface MeasureState {
  picking: boolean;
  points: Vec3[];
  /** One entry per picked point, in the same order. */
  provenance: SurfaceProvenance[];
  onPick: (point: Vec3, provenance: SurfaceProvenance) => void;
  onMiss: () => void;
  snap: boolean;
}

/** Everything the viewer needs to draw and feed the measurement overlay. */
export function useMeasureState(): MeasureState & {
  reset: () => void;
  setPicking: (on: boolean) => void;
  setSnap: (on: boolean) => void;
  missed: boolean;
} {
  const [picking, setPicking] = useState(false);
  const [snap, setSnap] = useState(true);
  const [points, setPoints] = useState<Vec3[]>([]);
  // Kept beside the points rather than inside them: the viewer's overlay draws
  // `points` and has no interest in where the surface came from, and a shape
  // change there would ripple through the picker and the projection.
  const [provenance, setProvenance] = useState<SurfaceProvenance[]>([]);
  const [missed, setMissed] = useState(false);

  const onPick = useCallback((point: Vec3, where: SurfaceProvenance) => {
    setMissed(false);
    // Two points at a time. A third starts a new measurement rather than
    // silently replacing one of the existing pair. The two arrays are reset
    // and appended together, so an entry can never describe the wrong point.
    setPoints((current) => (current.length >= 2 ? [point] : [...current, point]));
    setProvenance((current) => (current.length >= 2 ? [where] : [...current, where]));
  }, []);

  return {
    picking,
    snap,
    points,
    provenance,
    missed,
    onPick,
    onMiss: useCallback(() => setMissed(true), []),
    reset: useCallback(() => {
      setPoints([]);
      setProvenance([]);
      setMissed(false);
    }, []),
    setPicking: useCallback((on: boolean) => {
      setPicking(on);
      setMissed(false);
    }, []),
    setSnap,
  };
}

export function MeasurePanel({
  jobId,
  mesh,
  measure,
}: {
  jobId: string;
  mesh: Mesh;
  measure: ReturnType<typeof useMeasureState>;
}) {
  const fetcher = useCallback(
    (signal: AbortSignal) => getScaleCalibration(jobId, { signal }),
    [jobId],
  );
  const { state, reload } = useAsync(fetcher);
  const calibration: ScaleCalibration | null = state.data ?? null;

  const [unit, setUnit] = useState<Unit>("mm");
  /**
   * The kept measurements for this model.
   *
   * Read after mount rather than during render: `localStorage` is not
   * available while a page is being rendered on a server, and a panel that
   * threw there would take the whole model screen with it.
   */
  const [kept, setKept] = useState<KeptMeasurement[]>([]);
  useEffect(() => setKept(readMeasurements(jobId)), [jobId]);
  const remember = useCallback(
    (next: KeptMeasurement[]) => {
      setKept(next);
      writeMeasurements(jobId, next);
    },
    [jobId],
  );
  const [reference, setReference] = useState("");
  const [checkAgainst, setCheckAgainst] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const geometry = useMemo(() => {
    const { min, max } = boundsOf(mesh.positions);
    return { min, max, diagonal: diagonalOf(min, max) };
  }, [mesh.positions]);

  const verified = calibration?.verified === true;
  const pair = measure.points.length === 2 ? measure.points : null;
  const pairDistance = pair ? modelDistance(pair[0]!, pair[1]!) : null;
  // The second half of "can this number be trusted". The first is the scale.
  const endpoints = measure.provenance.slice(0, 2);
  const surfaceVerified =
    endpoints.length === 2 && measurementIsVerified(...endpoints);
  const unmeasuredEnds = endpoints.filter(
    (value) => !SURFACE_PROVENANCE_TERMS[value].measurable,
  );
  const dimensions = dimensionsOf(geometry.min, geometry.max, calibration, unit);
  /** One axis of the bounding box, formatted like every other length. */
  const extent = dimensions
    ? (axis: 0 | 1 | 2) =>
        formatLength(geometry.max[axis] - geometry.min[axis], calibration, unit) ?? "—"
    : null;

  async function calibrate() {
    if (!pair) return;
    const value = Number(reference.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      setError("Gerçek uzunluk sıfırdan büyük bir sayı olmalı.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setScaleCalibration(jobId, {
        point_a: pair[0]!,
        point_b: pair[1]!,
        reference_length: value,
        reference_unit: unit,
        model_diagonal: geometry.diagonal,
      });
      measure.reset();
      setReference("");
      reload();
    } catch {
      setError(
        "Ölçek belirlenemedi. İki noktanın birbirine çok yakın olmadığından ve girdiğiniz değerin doğru olduğundan emin olun.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function forget() {
    setBusy(true);
    setError(null);
    try {
      await clearScaleCalibration(jobId);
      measure.reset();
      reload();
    } catch {
      setError("Ölçek temizlenemedi. Tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }

  const check =
    pairDistance !== null && checkAgainst
      ? verificationError(
          pairDistance,
          Number(checkAgainst.replace(",", ".")),
          calibration,
          unit,
        )
      : null;

  return (
    <section className="card stack-tight" aria-label="Ölçüm ve ölçek">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Ölçüm</h3>
        <StatusBadge tone={verified ? "success" : "neutral"}>
          {verified ? "✓ Ölçek doğrulandı" : "Ölçek doğrulanmadı"}
        </StatusBadge>
      </div>

      {!verified && (
        <Callout title="Bu model henüz ölçeklenmedi">
          Fotogrametri modelleri biçim olarak doğru, boyut olarak keyfîdir. Gerçek
          uzunluğunu bildiğiniz iki noktayı seçip ölçüsünü girin; bundan sonraki
          tüm ölçümler buna göre hesaplanır.
        </Callout>
      )}

      <div className="row">
        <Button
          variant={measure.picking ? "primary" : "secondary"}
          onClick={() => measure.setPicking(!measure.picking)}
          ariaLabel="Nokta seçme modunu aç veya kapat"
        >
          {measure.picking ? "Seçmeyi durdur" : "İki nokta seç"}
        </Button>
        <Button variant="secondary" onClick={measure.reset} disabled={!measure.points.length}>
          Temizle
        </Button>
        <label className="inline-field">
          <input
            type="checkbox"
            checked={measure.snap}
            onChange={(event) => measure.setSnap(event.target.checked)}
          />
          <span>Köşeye yapıştır</span>
        </label>
      </div>

      {measure.picking && (
        <p className="muted">
          Model üzerinde {measure.points.length === 0 ? "birinci" : "ikinci"} noktaya
          tıklayın. Modeli döndürmek için sürüklemeye devam edebilirsiniz.
        </p>
      )}
      {measure.missed && (
        <p className="muted" role="status">
          Tıkladığınız yerde yüzey yok. Modelin üzerine tıklayın.
        </p>
      )}

      {pairDistance !== null && (
        <>
          <DataList
            items={[
              {
                term: "Seçilen iki nokta arası",
                value:
                  formatLength(pairDistance, calibration, unit) ??
                  "ölçek doğrulanmadı",
              },
              {
                term: "Ölçülen yüzey",
                value: endpoints
                  .map((value) => SURFACE_PROVENANCE_TERMS[value].label)
                  .join(" · "),
              },
            ]}
          />
          {/*
            Offered only where the number means something. Keeping an
            uncalibrated measurement would keep a model-space figure nobody can
            read, and the panel would fill with rows saying "ölçek
            doğrulanmadı".
          */}
          {pair && verified && (
            <div className="row">
              <Button
                variant="secondary"
                onClick={() =>
                  remember(
                    keepMeasurement(kept, {
                      points: [pair[0]!, pair[1]!],
                      provenance: [
                        endpoints[0] ?? "unverified",
                        endpoints[1] ?? "unverified",
                      ],
                      modelLength: pairDistance!,
                      note: "",
                    }),
                  )
                }
              >
                Bu ölçümü kaydet
              </Button>
            </div>
          )}
          {/*
            Shown whenever an end of the measurement is not a photograph of the
            world. Two different sentences, because "a model produced this" and
            "nobody recorded where this came from" are different facts and only
            the first is a claim about AI.
          */}
          {unmeasuredEnds.length > 0 && (
            <Callout
              tone="caution"
              title={
                unmeasuredEnds.includes("ai_completed")
                  ? "Bu ölçüm gerçek çekimle doğrulanmamış bir bölge içeriyor"
                  : "Bu ölçümün dayandığı yüzeyin kaydı yok"
              }
            >
              {unmeasuredEnds.includes("ai_completed")
                ? "Seçtiğiniz noktalardan en az biri, hiçbir fotoğrafın görmediği ve yapay zekâ ile tamamlanmış bir yüzeyde. Bu sayı bir ölçüm değil, bir tahmindir."
                : "Seçtiğiniz noktalardan en az birinin hangi çekimden geldiği kaydedilmemiş. Bu, yapay olduğu anlamına gelmez; doğrulanmadığı anlamına gelir."}
            </Callout>
          )}
        </>
      )}

      {/* Calibration: only offered when two points are actually selected. */}
      {pair && !verified && (
        <div className="stack-tight">
          <label className="field">
            <span className="field__label">Bu iki nokta arası gerçekte ne kadar?</span>
            <div className="row">
              <input
                inputMode="decimal"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder="125"
                aria-label="Gerçek uzunluk"
              />
              <select
                value={unit}
                onChange={(event) => setUnit(event.target.value as Unit)}
                aria-label="Birim"
              >
                {UNITS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <Button variant="primary" busy={busy} onClick={() => void calibrate()}>
                Ölçeği belirle
              </Button>
            </div>
            <small className="field__hint">
              Doğruluk için olabildiğince uzun ve kumpas ya da cetvelle güvenilir
              ölçülmüş bir referans seçin.
            </small>
          </label>
        </div>
      )}

      {verified && (
        <>
          <DataList
            items={[
              {
                term: "Referans",
                value: `${calibration!.reference_length} ${calibration!.reference_unit}`,
              },
              // Through `formatLength` like every other number here, so the
              // dimensions carry the same decimal separator and the same
              // precision cap as a measurement does. `toFixed` would print a
              // dot beside a comma two rows above it.
              ...(extent
                ? [
                    { term: "Genişlik", value: extent(0) },
                    { term: "Yükseklik", value: extent(1) },
                    { term: "Derinlik", value: extent(2) },
                  ]
                : []),
            ]}
          />
          <div className="row">
            <label className="inline-field">
              <span>Birim</span>
              <select
                value={unit}
                onChange={(event) => setUnit(event.target.value as Unit)}
                aria-label="Görüntüleme birimi"
              >
                {UNITS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <Button variant="secondary" busy={busy} onClick={() => void forget()}>
              Yeniden kalibre et
            </Button>
          </div>

          {/* The only real check. The first reference matches itself by
              construction, so its error is always zero. */}
          {pair && (
            <label className="field">
              <span className="field__label">Ölçeği ikinci bir mesafeyle doğrula</span>
              <div className="row">
                <input
                  inputMode="decimal"
                  value={checkAgainst}
                  onChange={(event) => setCheckAgainst(event.target.value)}
                  placeholder="80"
                  aria-label="Beklenen uzunluk"
                />
                <span className="faint">{unit}</span>
              </div>
              {check && (
                <small className="field__hint">
                  Ölçülen {formatLength(pairDistance!, calibration, unit)} ·
                  fark {check.absolute.toFixed(2)} {unit} ({check.relative.toFixed(2)}%)
                </small>
              )}
              <small className="field__hint">
                İlk referans kendisiyle birebir eşleşir; gerçek doğruluk ancak
                bağımsız ikinci bir mesafeyle görülür.
              </small>
            </label>
          )}
        </>
      )}

      {error && <Callout tone="danger">{error}</Callout>}

      {/*
        The kept list. Present only once something is in it: an empty "Kayıtlı
        ölçümler" heading on a screen where nobody has measured anything is a
        section that teaches people to skip it.
      */}
      {kept.length > 0 && (
        <div className="stack-tight">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h4 style={{ margin: 0 }}>Kayıtlı ölçümler</h4>
            <Button variant="secondary" onClick={() => remember([])}>
              Tümünü temizle
            </Button>
          </div>
          <ul className="measure-list">
            {kept.map((item, index) => {
              const ends = item.provenance.filter(
                (value) => !SURFACE_PROVENANCE_TERMS[value].measurable,
              );
              return (
                <li key={item.id} className="measure-list__row">
                  <span className="measure-list__name">
                    {measurementLabel(index)}
                  </span>
                  <span className="measure-list__value">
                    {/*
                      Re-converted through the *current* calibration, which is
                      why the stored value is a model-space length. A
                      recalibration corrects every row here with it.
                    */}
                    {formatLength(item.modelLength, calibration, unit) ??
                      "ölçek doğrulanmadı"}
                  </span>
                  {ends.length > 0 && (
                    <span className="measure-list__flag" title={ends.map((v) => SURFACE_PROVENANCE_TERMS[v].note).join(" ")}>
                      {ends.includes("ai_completed")
                        ? "ölçüm değil"
                        : "kaydı yok"}
                    </span>
                  )}
                  <Button
                    variant="secondary"
                    ariaLabel={`${measurementLabel(index)} ölçümünü sil`}
                    onClick={() => remember(dropMeasurement(kept, item.id))}
                  >
                    Sil
                  </Button>
                </li>
              );
            })}
          </ul>
          <p className="faint">
            Bu ölçümler yalnızca bu tarayıcıda saklanır. Ölçek ise projeyle
            birlikte sunucuda tutulur.
          </p>
        </div>
      )}

      {/* Three separate claims, and this product measures two of them. */}
      <p className="faint">
        {verified
          ? "Ölçek doğrulandı. Mesh'in baskı uygunluğu (su geçirmezlik, manifold yapı) ölçülmedi."
          : "Ölçek doğrulanmadan dışa aktarılan dosyalar 1:1 baskı için ölçekli değildir."}
      </p>
      {/*
        Only once a pair exists, and only as a positive statement. A permanent
        badge saying "surface verified" on a screen with nothing measured on it
        is a claim about nothing.
      */}
      {pair && surfaceVerified && verified && (
        <p className="faint">
          Bu ölçüm, gerçekten fotoğraflanmış bir yüzey üzerinde ve doğrulanmış bir
          ölçekle alındı.
        </p>
      )}
    </section>
  );
}
